import { store } from "@base/store";
import { homeActions } from "@components/features/home/home.slice";
import { LoaderSnackbarWrapperActions } from "@components/organisms/LoaderSnackbarWrapper/LoaderSnackbarWrapper.slice";
import { auth } from "@config/firebase/firbaseConfig";
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  Method,
} from "axios";
import { browserLocalPersistence, setPersistence } from "firebase/auth";
import { signOut } from "firebase/auth";

export const HTTP_STATUS_OK = 200;
export const HTTP_STATUS_MULTIPLE_CHOICES = 300;
export const HTTP_STATUS_UNAUTHORIZED = 401;
export const HTTP_STATUS_ERROR_RESPONSE = 500;
export const HTTP_STATUS_PAYMENT_REQUIRED = 402;

export type Response = AxiosResponse;

interface ErrorResponse {
  detail?: string;
  error?: string;
  // Add other properties as needed
}

const validateStatus = (status: number): boolean => {
  return status >= HTTP_STATUS_OK && status < HTTP_STATUS_MULTIPLE_CHOICES;
};

const validateErrorStatusCode = (code: number): boolean => {
  return code >= 400 && code < 600 && !(code === 401 || code === 402);
};

const checkStatus = (response: AxiosResponse): AxiosResponse | null => {
  const { status } = response;

  if (validateStatus(status)) {
    return response;
  }

  const error = new Error(response.statusText);
  error.message = response.data;
  throw error;
};

const checkError = (err: AxiosError<ErrorResponse>): Response => {
  const currentUser: string | null = localStorage.getItem("currentUser");

  const { response } = err;
  const error: AxiosError<ErrorResponse> = { ...err };

  if (response?.status === HTTP_STATUS_PAYMENT_REQUIRED) {
    store.dispatch(homeActions.setHasUserExceededPlan(true));
    store.dispatch(homeActions.setPlanMsg(response?.data?.detail!));
  }

  if (response?.status === HTTP_STATUS_UNAUTHORIZED) {
    auth.currentUser
      ?.getIdToken(true)
      .then((jwt) => {
        store.dispatch(
          homeActions.setUser({
            token: `Bearer ${jwt}`,
          })
        );
        if (currentUser) {
          const retrievedUser = JSON.parse(currentUser);
          const newUser = {
            ...retrievedUser,
            token: `Bearer ${jwt}`,
          };
          localStorage.setItem("currentUser", JSON.stringify(newUser));
        }
        window.location.reload();
      })
      .catch(() => {
        signOut(auth).then(() => {
          store.dispatch(homeActions.reset);
          localStorage.clear();
          window.location.href = "/";
        });
      });
  }

  if (validateErrorStatusCode(response?.status!)) {
    store.dispatch(
      LoaderSnackbarWrapperActions.setGlobalAppearances({
        alert: {
          isVisible: true,
          message: error.response?.data?.detail || error.response?.data?.error,
          type: "error",
        },
      })
    );
  }
  throw error;
};

export default function request(
  opt: AxiosRequestConfig,
  body: any
): Promise<AxiosResponse | null> {
  const options: AxiosRequestConfig = { ...opt };

  if (!options.headers) {
    options.headers = {};
  }

  if (body && typeof body === "object") {
    options.data = JSON.stringify(body);
  }

  return axios(options).then(checkStatus).catch(checkError);
}

const getToken = () => {
  const currentUser: string | null = localStorage.getItem("currentUser");
  if (currentUser) {
    var retrievedObject = JSON.parse(currentUser);
    return retrievedObject?.token;
  } else {
    return store.getState().user.user.token;
  }
};

export const baseHttp =
  (contentType: string) =>
  (method: Method) =>
  (url: string) =>
  (body: any) =>
  (withAuthorization: boolean): Promise<Response | null> => {
    const baseOptions: AxiosRequestConfig = {
      url,
      method,
    };
    const token: string | null | undefined = `${getToken()}`;
    const options: AxiosRequestConfig = {
      ...baseOptions,
      headers: {
        Accept: "application/json",
        "Content-Type": contentType,
      },
    };
    if (withAuthorization && !!token) {
      options.headers = {
        ...options.headers,
        Authorization: token,
      };
    }
    return request(options, body);
  };

export const baseHttpJson = baseHttp("application/json");
export const baseHttpZip = baseHttp("multipart/form-data");
export const baseHttpFormData = baseHttp(
  "application/x-www-form-urlencoded;charset=UTF-8"
);

export const httpGet = (url: string, withAuthorization: boolean) =>
  baseHttpJson("GET")(`${url}`)(undefined)(withAuthorization);

export const httpPost = (url: string, body: any, withAuthorization: boolean) =>
  baseHttpJson("POST")(`${url}`)(body)(withAuthorization);

export const httpPut = (url: string, body: any, withAuthorization: boolean) =>
  baseHttpJson("PUT")(`${url}`)(body)(withAuthorization);

export const httpPatch = (url: string, body: any, withAuthorization: boolean) =>
  baseHttpJson("PATCH")(`${url}`)(body)(withAuthorization);

export const httpDelete = (
  url: string,
  withAuthorization: boolean,
  body?: any
) => baseHttpJson("DELETE")(`${url}`)(body)(withAuthorization);

export const httpPostFormData = (
  url: string,
  body: any,
  withAuthorization: boolean
) => baseHttpFormData("POST")(`${url}`)(body)(withAuthorization);

export const httpPutFormData = (
  url: string,
  body: any,
  withAuthorization: boolean
) => baseHttpFormData("PUT")(`${url}`)(body)(withAuthorization);

export const httpDeleteFormData = (
  url: string,
  body: any,
  withAuthorization: boolean
) => baseHttpFormData("DELETE")(`${url}`)(body)(withAuthorization);

export const httpPostMultiPart = (url: string, body: any) =>
  axios.post(`${url}`, body, {
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: getToken(),
    },
  });
